import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { isEqual, omit } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import Typography from '@mui/material/Typography';
import { AccountData, AccountService, OrderPageData, OrderPageTypes } from '../../../api';
import { OrderPageUpdate } from '../../OrderPageSettingsPage';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { MaxStationsRestrictionTable } from '../../MaxStationsRestrictionTable';
import { OrderPagesMultiSelect } from '../../../userSettings/OrderPagesMultiSelect';
import { FormCheckbox } from '../../formComponents/FormCheckBox';
import { SettingsRow } from '../../formComponents/SettingsRow';
import { SharedResourcesAllocationTable } from './SharedResourcesAllocationTable';
import { CustomerChoosableResourcesAllocationTable } from './CustomerChoosableResourcesAllocationTable';
import { SectionSaveRegistrar } from '../../saveHeader/SectionSaveRegistrar';
import type { ChangeEntry } from '../../saveHeader/SaveHeaderContext';
import { collect, diffBoolean, diffIdList } from '../../saveHeader/diffBuilders';
import { SettingsPageTabs } from '../../SettingsPageTabs';
import { getOrderPages } from '../../../utils/AccountUtils';
import { useLanguage } from '../../../common/GeneralUtils';

type RestrictionsSettingsSectionProps = {
  orderPage: OrderPageData;
  orderPageInitialValues: OrderPageUpdate;
  updateLocalAccount: KeyedMutator<AccountData>;
  account: AccountData;
};

export type RestrictionsFormValues = {
  availability_settings: {
    parallel_order_page_ids: string[];
    blocks_private_when_empty: boolean;
  };
  shared_resources_allocations?: {
    automatic_dividable_resources: {
      resource_id: string;
      unit_capacity: number;
      total_units: number;
    }[];
    customer_choosable_resources: {
      resource_id: string;
      total_units: number;
      default: number;
    }[];
  };
};

export const RestrictionsSection = ({
  orderPage,
  orderPageInitialValues,
  updateLocalAccount,
  account,
}: RestrictionsSettingsSectionProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();
  const isEventLikeOrderPage =
    orderPage.order_page_type === OrderPageTypes.EVENT || orderPage.order_page_type === OrderPageTypes.COURSE;

  const validationSchema = yup.object().shape({
    availability_settings: yup.object().shape({
      parallel_order_page_ids: yup.array().of(yup.string()),
      blocks_private_when_empty: yup.boolean(),
    }),
    shared_resources_allocations: yup.object().shape({
      automatic_dividable_resources: yup.array().of(
        yup.object().shape({
          resource_id: yup.string().required(),
          unit_capacity: yup
            .number()
            .min(1)
            .test('max-capacity', t('sharedResources.maxCapacity'), (value, context) => {
              const { resource_id } = context.parent;
              const maxCapacity = account.shared_resources?.automatic_dividable_resources?.find(
                (r) => r.id === resource_id,
              )?.unit_capacity;
              return !value || !maxCapacity || value <= maxCapacity;
            }),
          total_units: yup
            .number()
            .min(1)
            .test('max-units', t('sharedResources.maxCapacity'), (value, context) => {
              const { resource_id } = context.parent;
              const maxUnits = account.shared_resources?.automatic_dividable_resources?.find(
                (r) => r.id === resource_id,
              )?.total_units;
              return !value || !maxUnits || value <= maxUnits;
            }),
        }),
      ),
      customer_choosable_resources: yup.array().of(
        yup.object().shape({
          resource_id: yup.string().required(),
          total_units: yup.number().min(1).required(),
          default: yup.number().min(0).required(),
        }),
      ),
    }),
  });

  const initialValues: RestrictionsFormValues = {
    availability_settings: {
      parallel_order_page_ids: orderPage.availability_settings.parallel_order_page_ids || [],
      blocks_private_when_empty: orderPage.availability_settings.blocks_private_when_empty || false,
    },
    shared_resources_allocations: {
      automatic_dividable_resources: orderPage.shared_resources_allocations?.automatic_dividable_resources || [],
      customer_choosable_resources: orderPage.shared_resources_allocations?.customer_choosable_resources || [],
    },
  };

  const handleSubmit = async (values: RestrictionsFormValues, { setStatus }: FormikHelpers<RestrictionsFormValues>) => {
    try {
      posthog.capture('Editing settings', { section: 'RestrictionsSection', settings: values });
      const updatedAccount = await AccountService.updateOrderPage(
        omit(
          {
            ...orderPageInitialValues,
            duration: (orderPageInitialValues.duration || 0) * 60,
            ...values,
            id: orderPage.id,
          },
          'availability_settings.restrictions',
          'objects',
        ),
      );
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const orderPageLabel = (id: string) => {
    const op = getOrderPages(account, true).find((o) => o.id === id);
    return op?.label?.[language] || id;
  };

  const getChanges = (values: RestrictionsFormValues, iv: RestrictionsFormValues): ChangeEntry[] => {
    const oldSR = iv.shared_resources_allocations;
    const newSR = values.shared_resources_allocations;
    const resourceRows: ChangeEntry[] = [];
    if (!isEqual(oldSR?.automatic_dividable_resources, newSR?.automatic_dividable_resources)) {
      resourceRows.push({
        label: t('sharedResources.title'),
        from: `${oldSR?.automatic_dividable_resources?.length || 0} allocations`,
        to: `${newSR?.automatic_dividable_resources?.length || 0} allocations`,
      });
    }
    if (!isEqual(oldSR?.customer_choosable_resources, newSR?.customer_choosable_resources)) {
      resourceRows.push({
        label: t('sharedResources.customerChoosableTitle'),
        from: `${oldSR?.customer_choosable_resources?.length || 0} allocations`,
        to: `${newSR?.customer_choosable_resources?.length || 0} allocations`,
      });
    }
    return [
      ...collect(
        diffIdList(
          t('parallelOrderPages.title'),
          iv.availability_settings?.parallel_order_page_ids,
          values.availability_settings?.parallel_order_page_ids,
          orderPageLabel,
        ),
        diffBoolean(
          t('blocksPrivateWhenEmpty.title'),
          iv.availability_settings?.blocks_private_when_empty,
          values.availability_settings?.blocks_private_when_empty,
        ),
      ),
      ...resourceRows,
    ];
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => {
        const { values, isSubmitting, errors, touched, handleChange } = formik;
        const formIsDisabled = readOnly || isSubmitting;
        const hasAutomaticResources = (account.shared_resources?.automatic_dividable_resources?.length || 0) > 0;
        const hasChoosableResources = (account.shared_resources?.customer_choosable_resources?.length || 0) > 0;
        const hasGeneralRow =
          isEventLikeOrderPage ||
          !!orderPage.availability_settings.restrictions_obj?.max_stations_restriction?.person_to_stations_capacities;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar tabKey={SettingsPageTabs.RESTRICTIONS} getChanges={getChanges} disabled={readOnly} />
            <Grid container flexDirection="column">
              {hasGeneralRow && (
                <SettingsRow label={t('tabs.restrictions')}>
                  <Grid container flexDirection="column" gap={2}>
                    {isEventLikeOrderPage && (
                      <Grid item>
                        <FormCheckbox
                          fieldName="availability_settings.blocks_private_when_empty"
                          disabled={formIsDisabled}
                          isFieldUpdate={false}
                          label={t('blocksPrivateWhenEmpty.title')}
                        />
                        <Typography variant="body2" color="text.secondary" display="block">
                          {t('blocksPrivateWhenEmpty.helper')}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item>
                      <MaxStationsRestrictionTable orderPage={orderPage} />
                    </Grid>
                  </Grid>
                </SettingsRow>
              )}
              <SettingsRow
                label={t('parallelOrderPages.title')}
                isLast={!hasAutomaticResources && !hasChoosableResources}>
                <OrderPagesMultiSelect
                  exceptOrderPageId={orderPage.id}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  orderPagesFieldName="availability_settings.parallel_order_page_ids"
                  account={account}
                />
              </SettingsRow>

              {hasAutomaticResources && (
                <SettingsRow label={t('sharedResources.title')} isLast={!hasChoosableResources}>
                  <SharedResourcesAllocationTable
                    values={values.shared_resources_allocations?.automatic_dividable_resources || []}
                    onChange={(newAllocations) => {
                      handleChange({
                        target: {
                          name: 'shared_resources_allocations.automatic_dividable_resources',
                          value: newAllocations,
                        },
                      });
                    }}
                    resources={account.shared_resources}
                    disabled={formIsDisabled}
                  />
                </SettingsRow>
              )}
              {hasChoosableResources && (
                <SettingsRow label={t('sharedResources.customerChoosableTitle')} isLast>
                  <CustomerChoosableResourcesAllocationTable
                    values={values.shared_resources_allocations?.customer_choosable_resources || []}
                    onChange={(newAllocations) => {
                      handleChange({
                        target: {
                          name: 'shared_resources_allocations.customer_choosable_resources',
                          value: newAllocations,
                        },
                      });
                    }}
                    resources={account.shared_resources}
                    disabled={formIsDisabled}
                  />
                </SettingsRow>
              )}
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
